import { compose } from 'recompose';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import withTokenAuth from '../Auth/withTokenAuth';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import {
    UPSERT_EVENT,
    INSERT_EXCEPTION,
    INSERT_EXCEPTION_EVENT,
    UPDATE_EXCEPTION,
} from '../OrderDetailsPage/graphql/mutations';
import ExceptionModal from '../OrderDetailsPage/modals/Exceptions';
import { useExceptionCallbacks } from '../OrderDetailsPage/hooks';
import { QUERY_BY_ORDER } from '@/graphql/queries/order_wh_events';
import { QUERY_BY_LEADER } from '@/graphql/queries/teammates';
import { captureException } from '@sentry/react';
import { useWarehouseAggregate } from '@onward-delivery/core';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { useMemo } from 'react';
import { addFormattedDateFields } from '@/utilities/addFormattedDateFields';
import { ORDER_BY_ID } from '../OrderDetailsPage/subscriptions';
import { omit } from 'lodash';
import { ITEM_READONLY_FIELDS, ORDER_READONLY_FIELDS } from '@onward-delivery/core';
import { CircularProgress, Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import DriverSummaryCard from './DriverSummaryCard';
import DriverCrossdockStepper from './DriverCrossdockStepper';
import DriverItemsList from './DriverItemsList';
import ChecklistModal from './modals/ChecklistModal';
import { OrderStatus } from './blocks';
import { useClientUser } from '@/hooks';
import { Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

const OrderDetailsPageDriver = ({ claims }) => {
    const { order_id } = useParams();
    // driver should container name, phone, and possibly driver_id
    const { driver, event_id } = claims;
    const { user_id } = useClientUser();

    const [order, setOrder] = useState({});
    const [exceptionParams, setExceptionParams] = useState(null);
    const [checklist, setChecklist] = useState({});
    const [notification, setNotification] = useState({});

    const {
        loading: orderLoading,
        error: orderError,
        data: orderData,
    } = useSubscription(ORDER_BY_ID, {
        variables: {
            order_id: order_id,
        },
        onData: ({ data }) => {
            const _order = data?.data?.orders?.[0];
            if (!_order) return;
            setOrder({ ..._order, ...addFormattedDateFields(_order) });
        },
        onError: (error) => {
            console.error(error);
            captureException(error);
        },
    });

    const [insertException, { loading: insertExceptionLoading }] = useMutation(INSERT_EXCEPTION, {
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to add exception. Please try again' });
        },
    });
    const [updateException, { loading: updateExceptionLoading }] = useMutation(UPDATE_EXCEPTION, {
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to update exception. Please try again' });
        },
    });

    const [insertExceptionEvent, { loading: insertExceptionEventLoading }] = useMutation(INSERT_EXCEPTION_EVENT, {
        update: (cache, { data: { mappings, order } }) => {
            if (order) {
                try {
                    cache.updateQuery(
                        {
                            query: ORDER_BY_ID,
                            variables: {
                                order_id: order_id,
                            },
                        },
                        (data) => {
                            if (!data?.orders?.[0]) return data;

                            const clone = {
                                ...data.orders[0],
                                ...order,
                            };

                            return {
                                ...data,
                                orders: [clone],
                            };
                        }
                    );
                } catch (e) {
                    console.debug('temp-driver: ORDER_BY_ID cache update skipped (exception)', e);
                }

                try {
                    cache.updateQuery(
                        {
                            query: QUERY_BY_ORDER,
                            variables: { order_id },
                        },
                        (data) => {
                            const existing = data?.results || [];
                            const next = order?.wh_events ? [...order.wh_events] : existing;

                            return {
                                ...data,
                                results: next,
                            };
                        }
                    );
                } catch (e) {
                    console.debug('temp-driver: QUERY_BY_ORDER cache update skipped (exception)', e);
                }
            }
        },
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to insert event. Please try again' });
        },
    });

    const [upsertEvent, { loading: eventUpsertLoading }] = useMutation(UPSERT_EVENT, {
        update: (cache, { data }) => {
            const updatedOrder = data?.order;
            const returningEvents = data?.events?.returning || [];

            if (updatedOrder) {
                try {
                    cache.updateQuery(
                        {
                            query: ORDER_BY_ID,
                            variables: {
                                order_id: order_id,
                            },
                        },
                        (prev) => {
                            if (!prev?.orders?.[0]) return prev;
                            return {
                                ...prev,
                                orders: [{ ...prev.orders[0], ...updatedOrder }],
                            };
                        }
                    );
                } catch (e) {
                    console.debug('temp-driver: ORDER_BY_ID cache update skipped', e);
                }
            }

            if (returningEvents.length) {
                try {
                    cache.updateQuery(
                        {
                            query: QUERY_BY_ORDER,
                            variables: { order_id },
                        },
                        (prev) => {
                            const existing = prev?.results || [];
                            const clone = [...existing];

                            for (const newEvent of returningEvents) {
                                const idx = clone.findIndex((e) => e.event_id === newEvent.event_id);
                                if (idx > -1) clone.splice(idx, 1, newEvent);
                                else clone.push(newEvent);
                            }

                            return {
                                ...prev,
                                results: clone,
                            };
                        }
                    );
                } catch (e) {
                    console.debug('temp-driver: QUERY_BY_ORDER cache update skipped', e);
                }
            }
        },
        onError: (error) => {
            captureException(error);
            setNotification({ severity: 'error', message: 'Failed to insert event. Please try again' });
        },
    });

    const { data: warehouseEvents } = useQuery(QUERY_BY_ORDER, {
        skip: !order.order_id,
        variables: { order_id },
    });
    const events = useMemo(() => {
        return warehouseEvents?.results || [];
    }, [warehouseEvents]);
    const warehouse = useWarehouseAggregate(warehouseEvents?.results);

    const derivedEventState = useMemo(() => {
        const last = events?.[events.length - 1];
        return last?.transition?.next || order?.event_state;
    }, [events, order?.event_state]);

    const orderWithEvents = useMemo(() => {
        return {
            ...order,
            // mirror OrderDetailsPage expectations
            wh_events: events,
            event_state: derivedEventState,
        };
    }, [order, events, derivedEventState]);

    const upsertEventCallback = (params = {}) => {
        const { event, events, order: update = {}, trackingEvents, tracking_events } = params;

        const rawEvents = events || (event ? [event] : []);
        const tracking = trackingEvents || tracking_events || [];
        const toInsert = (rawEvents || []).map((e) => {
            const { __typename, state, transition, driver, wh_driver, location, exceptions, ...remaining } = e || {};
            return {
                ...remaining,
                order_id: remaining?.order_id || order.order_id,
            };
        });

        const updateCols = Object.keys(toInsert[0] || {});
        console.debug('temp-driver upsertEvent', {
            events: toInsert,
            update_cols: updateCols,
            order_update_cols: Object.keys(update || {}),
            tracking_events: tracking,
        });

        return upsertEvent({
            variables: {
                events: toInsert,
                order: { ...update, order_id: order.order_id },
                update_cols: updateCols,
                order_update_cols: Object.keys(update),
                tracking_events: tracking,
            },
        });
    };

    const { data: teammates } = useQuery(QUERY_BY_LEADER, {
        skip: !order.carrier_id,
        variables: { leader_id: order.carrier_id },
    });
    const drivers = useMemo(() => {
        return (teammates?.results || []).filter((teammate) => teammate?.roles?.driver);
    }, [teammates]);

    const { createException } = useExceptionCallbacks(
        { order, exceptionParams, user_id },
        { insertExceptionEvent, insertException, updateException }
    );

    if (orderLoading) {
        return (
            <div
                css={css`
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <CircularProgress size={100} />
            </div>
        );
    }

    if (order?.routes?.length > 0) {
        return (
            <div
                css={css`
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <div
                    css={css`
                        font-size: 22px;
                        font-weight: 500;
                        width: 80%;
                        text-align: center;
                    `}
                >
                    This order has now been routed and this page can no longer be used to view and modify the order.
                </div>
            </div>
        );
    }

    return (
        <>
            <Grid
                container
                css={css`
                    width: 100%;
                    justify-content: center;
                    padding: 12px;
                `}
            >
                <OrderStatus status={orderWithEvents?.order_status} />
            </Grid>
            <DriverSummaryCard order={orderWithEvents} events={events} />
            <DriverCrossdockStepper
                order={orderWithEvents}
                events={events}
                isLoading={eventUpsertLoading}
                warehouse={warehouse}
                callbacks={{
                    editCrossdockPickup: (params) => {
                        console.debug('not available');
                    },
                    editCrossdock: (params) => {
                        console.debug('not available');
                    },
                    upsertEvent: upsertEventCallback,
                    createException: (params) => {
                        setExceptionParams(params);
                    },
                    confirmAction: (content, callback) => {
                        console.debug('not available');
                    },
                    openChecklist: (params, callback) => {
                        setChecklist({
                            ...params,
                            callback,
                        });
                    },
                    sendWeblink: (event) => {
                        console.debug('not available');
                    },
                    gotoFileTab: () => {
                        const wantsPOD = !!orderWithEvents?.ltl_dropoff_arrival && !(orderWithEvents?.pod || []).length;
                        const wantsPOP =
                            !!orderWithEvents?.loading_completed &&
                            !(orderWithEvents?.proof_of_pickup || []).length &&
                            !wantsPOD;

                        const doc = wantsPOP
                            ? {
                                  field: 'proof_of_pickup',
                                  name: 'Proof of Pickup',
                                  required: true,
                              }
                            : {
                                  field: 'pod',
                                  name: 'Proof of Delivery (POD)',
                                  required: true,
                              };

                        const title = wantsPOP ? 'Upload proof of pick up' : 'Upload POD';

                        const callback = (orderUpdates = {}) => {
                            return upsertEventCallback({
                                order: orderUpdates,
                            });
                        };

                        setChecklist({
                            title,
                            content: (
                                <div
                                    css={css`
                                        font-size: 14px;
                                    `}
                                >
                                    Upload the required document to continue.
                                </div>
                            ),
                            showItems: false,
                            documents: [doc],
                            signatures: [],
                            timestamps: [],
                            itemsHelperText: '',
                            itemEvent: null,
                            callback,
                        });
                    },
                }}
            />
            <DriverItemsList order={orderWithEvents} />
            <ChecklistModal
                {...checklist}
                open={checklist.content}
                order={orderWithEvents}
                items={orderWithEvents.itemsByOrderId}
                callbacks={{
                    onClose: () => setChecklist({}),
                    onConfirm: (...params) => checklist.callback(...params).then(() => setChecklist({})),
                    onError: (e) => {
                        setNotification({
                            severity: 'error',
                            message: e?.message || 'Failed to complete checklist. Please try again.',
                        });
                    },
                }}
            />
            <ExceptionModal
                {...exceptionParams}
                loading={
                    insertExceptionEventLoading ||
                    updateExceptionLoading ||
                    insertExceptionLoading ||
                    eventUpsertLoading
                }
                order={order}
                callbacks={{
                    onError: (e) => {
                        setNotification({
                            severity: 'error',
                            message: 'Failed to upload image. Please try again',
                        });
                    },
                    onSubmitCreateAnother: ({ exception }) => {
                        return createException(exception).then(() => {
                            setNotification({
                                severity: 'success',
                                message: 'Exception reported.',
                            });
                        });
                    },
                    onSubmit: ({ exception }) => {
                        return createException(exception).then(() => {
                            setNotification({
                                severity: 'success',
                                message: 'Exception reported.',
                            });
                            setExceptionParams(null);
                        });
                    },
                    onClose: () => setExceptionParams(null),
                }}
            />

            <Snackbar
                open={!!notification?.message}
                onClose={() => setNotification({})}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                autoHideDuration={10000}
            >
                <Alert onClose={() => setNotification({})} severity={notification?.severity || 'error'} elevation={6}>
                    {notification?.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default compose(withTokenAuth({ role: 'temp-driver', reqClaims: ['order_id'] }))(OrderDetailsPageDriver);
